import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import { CircleIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";

/**
 * Migrated from `@radix-ui/react-radio-group` to Base UI.
 *
 * The public API of both exports is unchanged, and so is every one of the 23
 * consumers: none of them styles through the state attribute, which is what
 * forced the 12-file sweep on Tabs (#6184).
 *
 * Absorbed here:
 *
 *  1. **The parts come from two packages now.** Radix kept `Root`, `Item` and
 *     `Indicator` together; Base UI has the group in
 *     `@base-ui/react/radio-group` and the item and its indicator in
 *     `@base-ui/react/radio`. `RadioGroupItem` keeps its name and its
 *     `data-slot`s, so the `[&_[data-slot=…]]` selectors that reach into it are
 *     unaffected.
 *  2. **`onValueChange` gains a second argument.** Base UI passes `eventDetails`
 *     alongside the value; forwarding it would widen the callback every caller
 *     is typed against.
 *  3. **The item is no longer a native button.** Base UI's `Radio.Root`
 *     defaults to `nativeButton: false`, rendering `role="radio"` plus a hidden
 *     input rather than a `<button>`. That is the better answer for form
 *     submission and is kept — but Tailwind's `disabled:` variant compiles to
 *     `&:disabled` and matches only a real disabled control, so
 *     `disabled:opacity-50` stopped applying. `data-[disabled]:` is carried
 *     alongside it, the same fix Accordion (#6176) and Tabs (#6184) needed.
 *     `radio-group.browser.test.tsx` asserts the dimming as a computed-opacity
 *     difference rather than a class, because only the pixels change.
 *
 * One layout fix goes with the element change. Radix's item was a `<button>`,
 * which centres its content; Base UI's is a `<span>`, which does not. The
 * indicator is a zero-height box — its only child is absolutely positioned — so
 * the dot rides whatever edge the indicator lands on. Measured: under Radix the
 * indicator sat at the item's vertical centre (y=24 in a 16px item at y=16),
 * under Base UI at its top (y=17), moving the dot 7px up. `flex items-center
 * justify-center` on the item restores the centring the button gave for free.
 *
 * `data-state="checked|unchecked"` becomes `data-checked` / `data-unchecked`.
 * Unusually for this initiative those mirror each other, and no consumer styles
 * through either, so the rename stays entirely inside this file.
 */
/**
 * Generic over the option type, which Radix's own typing allowed by accident
 * and callers depend on: `ProcessPickTicketBOLConfigRenderer` passes an
 * `onValueChange` narrowed to its own `SendTo` union. A wrapper fixed at
 * `string` rejects that under `strictFunctionTypes`, so the parameter is
 * threaded through instead — which also removes any need to cast the value
 * coming back out of Base UI.
 */
function RadioGroup<Value extends string = string>({
  className,
  onValueChange,
  ...props
}: Omit<RadioGroupPrimitive.Props<Value>, "onValueChange" | "className"> & {
  onValueChange?: (value: Value) => void;
  className?: string;
}) {
  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      onValueChange={(value: Value) => onValueChange?.(value)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: Omit<RadioPrimitive.Root.Props, "value" | "className"> & {
  value: string;
  className?: string;
}) {
  return (
    <RadioPrimitive.Root
      data-slot="radio-group-item"
      className={cn(
        "flex items-center justify-center border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon
          weight="fill"
          className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2"
        />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  );
}

export { RadioGroup, RadioGroupItem };
